import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { motion } from 'framer-motion';
import {
  CircleAlert,
  CheckCircle2,
  XCircle,
  Info,
  Clock,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import type { NotificationType } from '@/stores/notification';

const notificationCardVariants = cva(
  'relative w-full rounded-xl shadow-md p-4 transition-all duration-200 overflow-hidden',
  {
    variants: {
      variant: {
        warning: 'bg-input border-sidebar-accent hover:bg-card',
        error: 'bg-input',
        success: 'bg-input',
        info: 'bg-input',
        pending: 'bg-input',
      },
      isRead: {
        true: 'opacity-70',
        false: 'opacity-100',
      },
    },
    defaultVariants: {
      variant: 'warning',
      isRead: false,
    },
  },
);

const iconMap: Record<NotificationType, React.ElementType> = {
  warning: CircleAlert,
  error: XCircle,
  success: CheckCircle2,
  info: Info,
  pending: Clock,
};

export interface NotificationCardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof notificationCardVariants> {
  type?: NotificationType;
  title: string;
  subtitle?: string;
  code?: string;
  department?: string;
  professionalName?: string;
  date?: string;
  timeRange?: string;
  createdAt?: string;
  isRead?: boolean;
  onClick?: () => void;
  onDismiss?: () => void;
  actionLabel?: string;
  onAction?: () => void;
  showDismiss?: boolean;
  highlightQuery?: string;
  customIcon?: React.ReactNode;
  children?: React.ReactNode;
}

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const NotificationCard = React.forwardRef<
  HTMLDivElement,
  NotificationCardProps
>(
  (
    {
      className,
      type = 'warning',
      title,
      subtitle,
      code,
      department,
      professionalName,
      date,
      timeRange,
      createdAt,
      isRead = false,
      onClick,
      onDismiss,
      actionLabel,
      onAction,
      showDismiss = true,
      highlightQuery,
      customIcon,
      children,
      ...props
    },
    ref,
  ) => {
    const Icon = iconMap[type] || CircleAlert;
    const indicatorColor = {
      warning: 'text-statusAmarelo',
      error: 'text-statusAmarelo',
      success: 'text-statusVerde',
      info: 'text-statusAmarelo',
      pending: 'text-statusAmarelo',
    };

    const handleDismiss = (e: React.MouseEvent) => {
      e.stopPropagation();
      onDismiss?.();
    };

    const handleAction = (e: React.MouseEvent) => {
      e.stopPropagation();
      onAction?.();
    };

    const highlightText = (value?: string) => {
      if (!value) return value;

      const query = highlightQuery?.trim();
      if (!query) return value;

      const regex = new RegExp(`(${escapeRegExp(query)})`, 'gi');
      const parts = value.split(regex);

      return parts.map((part, index) => {
        if (part.toLowerCase() === query.toLowerCase()) {
          return (
            <mark
              key={`${part}-${index}`}
              className="rounded-sm bg-yellow-200/80 px-0.5 text-current dark:bg-yellow-500/30"
            >
              {part}
            </mark>
          );
        }

        return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>;
      });
    };

    return (
      <div ref={ref} {...props}>
        <motion.div
          className={cn(
            notificationCardVariants({ variant: type, isRead }),
            className,
          )}
          onClick={onClick}
          initial={{ opacity: 0, y: 10, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, x: 50, scale: 0.95 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-inativo font-medium">
              {createdAt}
            </span>
            {showDismiss && (
              <button
                onClick={handleDismiss}
                className="p-1 rounded-full hover:bg-muted/50 transition-colors opacity-60 hover:opacity-100"
                aria-label="Dispensar notificação"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <div className="flex gap-3">
            <div className="flex items-center justify-center w-5 h-5 rounded-full shrink-0">
              <Icon className={indicatorColor[type]} />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <h4 className="font-semibold text-sm text-card-foreground leading-tight">
                  {highlightText(title)}
                </h4>
                {department && (
                  <span className="text-sm text-muted-foreground">
                    ({highlightText(department)})
                  </span>
                )}
              </div>

              {(code || subtitle) && (
                <div className="flex items-center gap-2 mt-1.5 text-xs text-card-foreground">
                  {code && (
                    <span className="font-medium">{highlightText(code)}</span>
                  )}
                  {code && subtitle && <span>|</span>}
                  {subtitle && <span>{highlightText(subtitle)}</span>}
                </div>
              )}

              {(professionalName || date || timeRange) && (
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1 text-xs text-card-foreground">
                  {professionalName && (
                    <span className="font-medium">
                      {highlightText(professionalName)}
                    </span>
                  )}
                  {professionalName && date && <span>|</span>}
                  {date && <span>{highlightText(date)}</span>}
                  {(professionalName || date) && timeRange && <span>|</span>}
                  {timeRange && <span>{highlightText(timeRange)}</span>}
                </div>
              )}

              {children && <div className="mt-2">{children}</div>}

              {actionLabel && onAction && (
                <div className="mt-3">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleAction}
                    className="h-7 text-xs px-3"
                  >
                    {actionLabel}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    );
  },
);

NotificationCard.displayName = 'NotificationCard';

export { NotificationCard, notificationCardVariants };
